"use client";

import { useRef } from "react";
import Image from "next/image";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import BlackPill from "@/components/getting-started/BlackPill";
import { CTAButton } from "@/shadcn/ui/button";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";
import { GroqImage } from "@/helpers/Image";

import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";

import griBGimg from "@/public/lp2/component1/grid-bg.svg";
import blurBGimg from "@/public/lp2/component4/blur-bg.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export default function Component4Section({ data }: { data: PageHomeSchema }) {
  const containerRef = useRef<HTMLDivElement | any>(undefined);
  const content1Ref = useRef<HTMLDivElement | any>(undefined);
  const content2Ref = useRef<HTMLDivElement | any>(undefined);
  const content3Ref = useRef<HTMLDivElement | any>(undefined);
  const content4Ref = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const component4TL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      component4TL
        .from(content1Ref.current.children, {
          duration: 1,
          stagger: 0.2,
          delay: 0.5,
          scrollTrigger: {
            trigger: content1Ref.current,
            end: "center center",
            scrub: 1,
          },
        })
        .from(content2Ref.current.children, {
          y: "2rem",
          stagger: 0.1,
          delay: 0.5,
          scrollTrigger: {
            trigger: content2Ref.current,
            end: "center center",
            scrub: 1,
          },
        })
        .from(content3Ref.current.children, {
          y: "2rem",
          stagger: 0.1,
          delay: 0.5,
          scrollTrigger: {
            trigger: content3Ref.current,
            end: "center center",
            scrub: 1,
          },
        })
        .from(content4Ref.current, {
          y: "2rem",
          delay: 0.5,
          scrollTrigger: {
            trigger: content4Ref.current,
            end: "center center",
            scrub: 1,
          },
        });
    },
    { scope: containerRef }
  );

  return (
    <section
      ref={containerRef}
      className="w-full relative z-[1] overflow-hidden component-4-section-bg-gradient mix-blend-lighten pt-[10rem] pb-[20rem] lg:pb-[30rem]"
      id="component-4"
    >
      <div className="container-lp mx-auto relative">
        {/* Backgrounds */}
        <div
          ref={content1Ref}
          className="relative w-full mb-[-16rem] md:mb-[-20rem] lg:mb-[-20rem] xl:mb-[-28rem] 2xl:mb-[-18rem]"
        >
          <HLSVideoPlayer
            controls={false}
            playsInline={true}
            autoPlay={true}
            muted={true}
            loop={true}
            className="w-full h-[70vh] lg:h-auto !max-w-[1920px] mx-auto object-cover pointer-events-none mix-blend-lighten"
            source={{
              embed: false,
              muxVideo: {
                assetId: "",
                filename: "video-bg",
                status: "ready",
                playbackId: "02JX4UzGOfT6fVeJguE02n6VGGLFc6ljE7ABhNxU8xtC4",
              },
              embedUrl: "",
              thumbnail: {
                src: "",
                width: 0,
                height: 0,
                lqip: "",
                name: "",
              },
            }}
          />

          <Image
            src={griBGimg}
            alt="Grid background"
            className="absolute z-[-3] left-0 right-0 bottom-0 w-full h-[50vh] lg:h-auto object-cover pointer-events-none"
          />

          <Image
            src={blurBGimg}
            alt="Blur background"
            className="absolute z-[-2] top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] pointer-events-none w-max max-w-max lg:w-full lg:max-w-full"
          />
        </div>
        {/* End Backgrounds */}

        <div className="flex flex-col items-center justify-center mx-auto relative z-[1]">
          <div
            ref={content2Ref}
            className="w-full md:w-4/5 lg:w-[95%] flex flex-col items-center justify-center mx-auto"
          >
            {data?.component4Texts && (
              <PortableText
                value={data?.component4Texts ?? []}
                components={customRichTextComponent({
                  block: {
                    h6: ({ children }) => (
                      <BlackPill text={children as string} />
                    ),
                    h2: ({ children }) => (
                      <h2 className="gradient-title text-[5rem] lg:text-[7rem] font-[600] tracking-[-0.35rem] lg:tracking-[-0.49rem] lg:leading-[8.875rem] leading-[110%] text-center mt-[4rem] mb-[3rem]">
                        {children}
                      </h2>
                    ),
                    normal: ({ children }) => (
                      <p className="text-[2.25rem] font-[400] leading-[150%] text-white opacity-75 text-center lg:w-[80%] mx-auto">
                        {children}
                      </p>
                    ),
                  },
                  marks: {
                    orangeFlare: ({ children }) => (
                      <span className="green-shadow">{children}</span>
                    ),
                  },
                })}
              />
            )}
          </div>

          <div className="w-full flex flex-col lg:flex-row justify-center items-center lg:gap-[4rem] mb-[-20rem]">
            <div
              ref={content3Ref}
              className="flex flex-col justify-center items-start lg:gap-[3rem] lg:basis-[35%] order-2 lg:order-1 mt-[-5rem] lg:mt-0"
            >

                {data?.component4ItemTexts && (
                  <PortableText
                    value={data?.component4ItemTexts ?? []}
                    components={customRichTextComponent({
                      block: {
                        normal: ({ children }) => (
                          <p className="text-white text-[2.5rem] font-[400] leading-[150%] text-center lg:text-left !mb-8">
                            {children}
                          </p>
                        ),
                      },
                      list: {
                        bullet: ({ children }) => (
                          <ul className="!list-disc !list-inside ml-[.5rem] mb-[1rem]">
                            {children}
                          </ul>
                        ),
                        number: ({ children }) => (
                          <ol className="">{children}</ol>
                        ),
                      },
                      listItem: {
                        bullet: ({ children }) => (
                          <li className="text-white text-[2.25rem] font-[500] leading-[150%] opacity-80 mb-[2rem]">
                            {children}
                          </li>
                        ),
                      },
                    })}
                  />
                )}
             

              {data?.component4CTA && <CTAButton data={data?.component4CTA} />}
            </div>

            <div
              ref={content4Ref}
              className="lg:basis-[65%] order-1 lg:order-2 flex justify-center items-center w-[200%] mt-[-10rem] lg:mt-0"
            >
              <GroqImage
                src={data?.component4Image}
                alt="Asset image"
                className="w-full max-w-[1451px] scale-[1.2] lg:scale-[1.5]"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
